// Live's text layers (spec G13b, PR D2; live's `text-layout.ts`, `text-fonts.ts`): ten Google
// Fonts families, a text wrapped at 80 % of the document's width with a line height of 1.2, its
// frame the measured box, and the same drawing on the canvas as in the saved image.
import { googleStylesheet, loadGoogleFace } from "../../../platform/google-fonts";
import type { Frame, Size, TextLayer } from "./model";
import { LayerError } from "./errors";

/** A text layer's own values: what the text controls change. */
export type TextValues = Pick<TextLayer, "text" | "font" | "weight" | "size" | "color" | "align">;

interface TextFont {
  family: string;
  category: "sans-serif" | "serif" | "monospace";
  bold: boolean;
}

/** Live's ten families, in its order: Inter the default, Bebas Neue without a bold. */
export const TEXT_FONTS = {
  inter: { family: "Inter", category: "sans-serif", bold: true },
  roboto: { family: "Roboto", category: "sans-serif", bold: true },
  montserrat: { family: "Montserrat", category: "sans-serif", bold: true },
  poppins: { family: "Poppins", category: "sans-serif", bold: true },
  oswald: { family: "Oswald", category: "sans-serif", bold: true },
  "bebas-neue": { family: "Bebas Neue", category: "sans-serif", bold: false },
  "playfair-display": { family: "Playfair Display", category: "serif", bold: true },
  lora: { family: "Lora", category: "serif", bold: true },
  merriweather: { family: "Merriweather", category: "serif", bold: true },
  "roboto-mono": { family: "Roboto Mono", category: "monospace", bold: true },
} as const satisfies Record<string, TextFont>;

export type TextFontId = keyof typeof TEXT_FONTS;
export const TEXT_FONT_IDS = Object.keys(TEXT_FONTS) as TextFontId[];

/** A text layer's own values. */
export function textValuesOf(layer: TextLayer): TextValues {
  const { text, font, weight, size, color, align } = layer;
  return { text, font, weight, size, color, align };
}

/** Live's bounds and defaults. */
export const MIN_TEXT_SIZE = 8;
export const MAX_TEXT_SIZE = 1024;
export const MAX_TEXT_LENGTH = 500;
export const TEXT_LINE_HEIGHT = 1.2;
const DEFAULT_SIZE_SHARE = 0.08;
/** A new text wraps inside the document less 10 % on each side. */
const DOCUMENT_INSET = 0.1;
const MIN_BOX = 4;
const HEX_COLOR = /^#[0-9a-f]{6}$/i;
/** A character's width as a share of the size, where a page cannot measure (a test's). */
const CHAR_SHARE = 0.55;

/** Live's words. */
export const TEXT_NOT_ADDED = "The text could not be added.";
export const TEXT_NOT_UPDATED = "The text could not be updated.";
const TEXT_INVALID = "The text settings are invalid.";
const FONT_UNAVAILABLE = "This font is not available.";

const WEIGHT = { regular: 400, bold: 700 } as const;

function fontOf(id: string): TextFont | undefined {
  return Object.hasOwn(TEXT_FONTS, id) ? TEXT_FONTS[id as TextFontId] : undefined;
}

/** Whether a font has the weight: every one has Regular, all but Bebas Neue Bold. */
export function hasWeight(font: string, weight: TextLayer["weight"]): boolean {
  const known = fontOf(font);
  return known !== undefined && (weight === "regular" || known.bold);
}

/** The weight to keep when the font changes: Regular where the new one has no Bold, as live's. */
export function supportedWeight(font: string, weight: TextLayer["weight"]): TextLayer["weight"] {
  return hasWeight(font, weight) ? weight : "regular";
}

/** Whether the values are live's valid ones. */
export function validText(values: TextValues): boolean {
  return (
    values.text.length <= MAX_TEXT_LENGTH &&
    hasWeight(values.font, values.weight) &&
    Number.isInteger(values.size) &&
    values.size >= MIN_TEXT_SIZE &&
    values.size <= MAX_TEXT_SIZE &&
    HEX_COLOR.test(values.color) &&
    ["left", "center", "right"].includes(values.align)
  );
}

/** Live's new text: "Add text" in Inter, #111111, centred, 8 % of the document's short side. */
export function defaultText(document: Size): TextValues {
  const size = Math.round(
    Math.min(MAX_TEXT_SIZE, Math.max(MIN_TEXT_SIZE, Math.min(document.width, document.height) * DEFAULT_SIZE_SHARE)),
  );
  const color = "#111111"; // raw-color: live's default text color, the layer's value, not a style
  return { text: "Add text", font: "inter", weight: "regular", size, color, align: "center" };
}

/** The CSS font a text is drawn in at `size`, its category the fallback. */
export function canvasFont(values: Pick<TextValues, "font" | "weight">, size: number): string {
  const font = fontOf(values.font) ?? TEXT_FONTS.inter;
  return `${WEIGHT[values.weight]} ${size}px "${font.family}", ${font.category}`;
}

/** Loads the face a text needs from Google Fonts; a font not in live's ten, or a weight it lacks,
 * is live's "This font is not available." */
export async function loadTextFont(values: Pick<TextValues, "font" | "weight" | "text">): Promise<void> {
  const font = fontOf(values.font);
  if (font === undefined || !hasWeight(values.font, values.weight)) throw new LayerError(FONT_UNAVAILABLE);
  const styles = font.bold ? [{ weight: 400, italic: false }, { weight: 700, italic: false }] : [{ weight: 400, italic: false }];
  await loadGoogleFace(
    { family: font.family, weight: WEIGHT[values.weight], italic: false, text: values.text },
    googleStylesheet(font.family, styles),
  );
}

/** How wide a string is in the font set, and the font's ascent and descent. */
export interface Measure {
  width: (shown: string) => number;
  ascent: number;
  descent: number;
}

function canvasContext(): CanvasRenderingContext2D | null {
  try {
    return document.createElement("canvas").getContext("2d");
  } catch {
    return null;
  }
}

/** A measure of the text at its own size: the canvas's, else a share of the size per character. */
export function measureOf(values: TextValues, context = canvasContext()): Measure {
  if (context === null) {
    return { width: (shown) => shown.length * CHAR_SHARE * values.size, ascent: values.size * 0.8, descent: values.size * 0.2 };
  }
  context.font = canvasFont(values, values.size);
  const sample = context.measureText("Mg");
  return {
    width: (shown) => context.measureText(shown).width,
    ascent: sample.fontBoundingBoxAscent || values.size * 0.8,
    descent: sample.fontBoundingBoxDescent || values.size * 0.2,
  };
}

export interface TextLayout {
  lines: { text: string; width: number }[];
  width: number;
  height: number;
  ascent: number;
  lineHeight: number;
}

/** A word longer than the line, cut where it no longer fits. */
function cut(measure: Measure, word: string, most: number): string[] {
  const pieces: string[] = [];
  for (const character of word) {
    const last = pieces.at(-1);
    if (last !== undefined && measure.width(last + character) <= most) pieces[pieces.length - 1] = last + character;
    else pieces.push(character);
  }
  return pieces;
}

/** One paragraph wrapped by words at `most`, as live's. */
function wrap(measure: Measure, paragraph: string, most: number): string[] {
  const words = paragraph.trim().split(/\s+/).filter((word) => word !== "");
  const pieces = words.flatMap((word) => (measure.width(word) > most ? cut(measure, word, most) : [word]));
  const lines: string[] = [];
  for (const piece of pieces) {
    const last = lines.at(-1);
    if (last !== undefined && measure.width(`${last} ${piece}`) <= most) lines[lines.length - 1] = `${last} ${piece}`;
    else lines.push(piece);
  }
  return lines.length > 0 ? lines : [""];
}

/** The text's lines and box at its own size, wrapped at `most` document pixels. */
export function layoutText(measure: Measure, values: TextValues, most: number): TextLayout {
  const lines = values.text
    .split(/\r?\n/)
    .flatMap((paragraph) => wrap(measure, paragraph, most))
    .map((text) => ({ text, width: measure.width(text) }));
  const lineHeight = Math.max(values.size * TEXT_LINE_HEIGHT, measure.ascent + measure.descent);
  return {
    lines,
    width: Math.max(MIN_BOX, ...lines.map((line) => line.width)),
    height: Math.max(MIN_BOX, lines.length * lineHeight),
    ascent: measure.ascent,
    lineHeight,
  };
}

/**
 * A text layer, its frame the measured box centred on `centre` (an edit keeps the text where it
 * was), else on the document. The font loads first, as the canvas does not wait for it.
 */
export async function textLayer(
  id: string,
  name: string,
  values: TextValues,
  document: Size,
  centre?: { x: number; y: number },
): Promise<TextLayer> {
  if (!validText(values)) throw new LayerError(TEXT_INVALID);
  await loadTextFont(values);
  const layout = layoutText(measureOf(values), values, document.width * (1 - DOCUMENT_INSET * 2));
  const x = centre?.x ?? document.width / 2;
  const y = centre?.y ?? document.height / 2;
  return {
    kind: "text",
    id,
    name,
    source: "text",
    ...values,
    frame: { x: x - layout.width / 2, y: y - layout.height / 2, width: layout.width, height: layout.height },
  };
}

/** Draws a text layer into `place`, its frame after scaling; it wraps at the unscaled frame, so a
 * scale never re-flows it. */
export function drawText(context: CanvasRenderingContext2D, layer: TextLayer, place: Frame): void {
  const scale = place.width / layer.frame.width;
  const layout = layoutText(measureOf(layer, context), layer, layer.frame.width);
  context.save();
  context.font = canvasFont(layer, layer.size * scale);
  context.fillStyle = layer.color;
  context.textAlign = "left";
  context.textBaseline = "alphabetic";
  for (const [at, line] of layout.lines.entries()) {
    const free = place.width - line.width * scale;
    const offset = layer.align === "center" ? free / 2 : layer.align === "right" ? free : 0;
    context.fillText(line.text, place.x + offset, place.y + (at * layout.lineHeight + layout.ascent) * scale);
  }
  context.restore();
}

/** Live's names: "Text", then "Text 2" and on, counting every text layer, removed ones too. */
export function nextTextName(layers: Record<string, { kind: string }>): string {
  const count = Object.values(layers).filter((layer) => layer.kind === "text").length;
  return count === 0 ? "Text" : `Text ${count + 1}`;
}
